import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
// import ShoppingCart from "@insite/mobius/Icons/ShoppingCart"; // Store Custom Code
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

/* Store Custom Code - Start */
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import ShoppingCart from "@store/Library/Components/Icons/ShopCart";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { resolveColor } from "@insite/mobius/utilities";
/* Store Custom Code - End */

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

/* Store Custom Code - Start */
const mapStateToProps = (state: ApplicationState) => {
    const currentPage = getCurrentPage(state);

    return {
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        currentPageType: currentPage.type,
    };
};
/* Store Custom Code - End */

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    /* Store Custom Code - Start */
    routerLink: {
        typographyProps: {
            css: css`
                position: absolute;
                background: ${getColor("primary.main")};
                border-radius: 25px;
                color: ${getColor("primary.contrast")} !important;
                text-align: center;

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            { values: ["-4px", null, null, "-8px", null], properties: ["top"] },
                            { values: ["-12px", null, null, "-18px", null], properties: ["right"] },
                            { values: ["20px", null, null, "22px", null], properties: ["width"] },
                            { values: ["20px", null, null, "22px", null], properties: ["height"] },
                            { values: ["10px", null, null, "12px", null], properties: ["font-size"] },
                            { values: ["3.5px", null, null, "3px", null], properties: ["padding-top"] },
                        ],
                        mediaOptions.min,
                    )}
            `,
        },
        icon: {
            iconProps: {
                css: css`
                    margin: 0;

                    svg {
                        width: 28px;
                        height: 28px;
                    }
                    ${({ theme }) =>
                        breakpointValuesMediaQueries(
                            theme,
                            [
                                {
                                    values: [
                                        `${resolveColor("secondary.main", theme)} !important`,
                                        null,
                                        null,
                                        `${resolveColor("secondary.contrast", theme)} !important`,
                                        null,
                                    ],
                                    properties: ["color"],
                                },
                                {
                                    values: ["32px", null, null, "28px", null],
                                    properties: ["width"],
                                },
                                {
                                    values: ["32px", null, null, "28px", null],
                                    properties: ["height"],
                                },
                            ],
                            mediaOptions.min,
                        )}
                `,
            },
        },
        css: css`
            position: relative;
        `,
    },
    /* Store Custom Code - End */
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    // Store Custom Code
    currentPageType,
}) => {
    useEffect(() => {
        /* Store Custom Code - Start */
        // dont load cart again if in cart page
        if (shouldLoadCart && currentPageType !== "CartPage") {
            loadCurrentCart();
        }
        /* Store Custom Code - End */
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    return (
        <Link
            {...routerLink}
            href={cartUrl}
            icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
            data-test-selector="cartLink"
        >
            {/* Store Custom Code - Start */}
            {showLabel && `${translate("Cart")} `}
            <span data-test-selector="cartLinkQuantity">{retainedTotalCountToDisplay}</span>
            <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            {/* Store Custom Code - End */}
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
